import { useProductsGraphs } from 'hooks/products/useGraphs';
import {
  useFunctionalCurrency,
  useProductsWithHoldingsAndRates,
} from 'hooks/products/useProducts';
import { useGetBundlesInfoQuery } from 'store/slices/cdn-api';
import { usePortfolioGainsAndLosses } from 'store/slices/gains-api';
import { useGetRatesQuery } from 'store/slices/rates-api';
import {
  buildAssetBreakdown,
  buildAvailableAssets,
  buildPortfolioSummary,
} from '../utils/dataBuilders';

/**
 * Shared hook that aggregates all portfolio/market data required by AI Insights queries.
 * Used by both useAskQuestionRequest and useAskConversationQuestion to avoid duplication.
 */
export function useAiInsightsContext() {
  const { functionalCurrency, isLoading: isLoadingFunctionalCurrency } =
    useFunctionalCurrency();
  const productGraph = useProductsGraphs('24h', {
    displayCurrency: functionalCurrency?.code || 'ZAR',
  });
  const { products, isLoading: isLoadingProducts } =
    useProductsWithHoldingsAndRates();
  const { gainsAndLosses, isLoading: isLoadingPortfolioGainsValue } =
    usePortfolioGainsAndLosses('24h');
  const { data: bundlesInfo, isLoading: isLoadingBundlesInfo } =
    useGetBundlesInfoQuery();
  const { data: rates, isLoading: isLoadingRates } = useGetRatesQuery({
    displayCurrency: functionalCurrency?.code || 'ZAR',
  });

  const currencyCode = functionalCurrency?.code || 'ZAR';
  const currencySymbol = functionalCurrency?.symbol || 'R';

  const isLoadingRequiredData =
    isLoadingFunctionalCurrency ||
    isLoadingProducts ||
    isLoadingPortfolioGainsValue ||
    isLoadingBundlesInfo ||
    isLoadingRates;

  const availableAssets = buildAvailableAssets(
    products,
    bundlesInfo,
    rates?.entities,
    productGraph,
    currencyCode,
  );
  const assetBreakdown = buildAssetBreakdown(products);
  const portfolioSummary = buildPortfolioSummary(products, gainsAndLosses);

  const productCodes = products?.map(p => p.code.toUpperCase()) ?? [];
  const productNames = products?.map(p => p.name) ?? [];

  return {
    currencyCode,
    currencySymbol,
    availableAssets,
    assetBreakdown,
    portfolioSummary,
    isLoadingRequiredData,
    productCodesAndNames: [...productCodes, ...productNames],
  };
}
